import { v4 as uuidv4 } from "uuid";
import { SearchResultData } from "../../app/slices/SearchResultsSlice";
import { InteractionFactoryEntity } from "../../features/interaction_factory/InteractionFactory";
import { GetMigratedProperties } from "../../features/project_migration/InteractionMigrationHelper";
import MessageBubblePreview from "../../features/previews/message_bubble/MessageBubblePreview";
import MessageBubbleNodePreview from "../../features/sequence/node_previews/MessageBubbleNodePreview";
import { ValidationHelper } from "../../features/validation/ValidationHelper";
import { Actor } from "../Actor";
import { InteractionProperty, InteractionPropertyType } from "../InteractionProperty";
import { Prop } from "../Prop";
import { IAnswer } from "../Quiz";
import Take from "../Take";
import Take3D from "../Take3D";
import Interaction, { InteractionType } from "./Interaction";
import {
    compactSearchableStrings,
    SearchLookupContext,
} from "./SearchableInteractionValues";
import { getBubbleTargetPropIds } from "../../helpers/BubbleTargetPropHelper";

export type MessageBubbleAnimation = {
    name: string;
    animationState: string;
    layer: number;
};

export const defaultMessageBubbleAnimation: MessageBubbleAnimation = {
    name: "",
    animationState: "Talk_1",
    layer: 0,
};

export const noMessageBubbleAnimation: MessageBubbleAnimation = {
    name: "",
    animationState: "none",
    layer: 0,
};

export function normalizeMessageBubbleAnimation(animation: MessageBubbleAnimation | string | undefined): MessageBubbleAnimation {
    if (!animation) return defaultMessageBubbleAnimation;
    if (typeof animation === "string") {
        return {
            name: "",
            animationState: animation,
            layer: 0,
        };
    }

    return {
        name: animation.name ?? "",
        animationState: animation.animationState ?? animation.name ?? defaultMessageBubbleAnimation.animationState,
        layer: animation.layer ?? 0,
    };
}

export function getMessageBubbleAnimationSearchValue(animation: MessageBubbleAnimation | string | undefined): string {
    const normalized = normalizeMessageBubbleAnimation(animation);
    return normalized.animationState || normalized.name;
}

export const getLookAtTargetMissingWarning = (actor: Actor): string =>
    `${actor.workingName} was not found in the current take. Select a different Look At target.`;

export const getBubbleAnchorMissingWarning = (anchorName: string): string =>
    `${anchorName} was not found in current take`;

export function validateBubbleAnchorInTake(
    anchorId: string,
    take: Take3D,
    actorPresets: Actor[],
    propPresets: Prop[],
    warnings: string[],
    targetInteractionId: string,
) {
    const actor = actorPresets.find((candidate) => candidate.id === anchorId);
    if (actor) {
        ValidationHelper.ValidateNpcInTake(anchorId, take, actorPresets, warnings);
        return;
    }

    const prop = propPresets.find((candidate) => candidate.id === anchorId);
    if (!prop) return;

    const availablePropIds = getBubbleTargetPropIds(take, {
        kind: "timeline",
        interactionId: targetInteractionId,
    });

    if (!availablePropIds.has(anchorId)) {
        warnings.push(getBubbleAnchorMissingWarning(prop.name ?? anchorId));
    }
}

export const messageBubbleStyles: string[] = [
    "Normal",
    "Thinking",
    "Yelling",
    "Shout",
    "Dialog_Action",
    "Dialog_Talk",
    "Mechanic",
    "Sleep",
];

export interface IMessageBubble extends Interaction {
    text: string;
    npcId: string;
    messageBubbleStyle: string;
    lookAt: string;
    animation: MessageBubbleAnimation | string;
}

export const requiredMessageBubbleProperties: InteractionProperty[] = [
    {
        id: "displayTime",
        name: "Display Time",
        type: InteractionPropertyType.number,
        value: 0,
    },
];

export function CreateMessageBubble(keys: string[]): IMessageBubble {
    return {
        id: uuidv4(),
        name: "Message Bubble",
        type: InteractionType.MessageBubble,
        text: keys[0],
        npcId: "player",
        lookAt: "",
        animation: { ...defaultMessageBubbleAnimation },
        messageBubbleStyle: messageBubbleStyles[0],
        properties: requiredMessageBubbleProperties,
    };
}

export function CreateMessageBubbleCopy(titleKey: string, interaction: IMessageBubble): IMessageBubble {
    return {
        id: uuidv4(),
        name: "Message Bubble",
        type: InteractionType.MessageBubble,
        text: titleKey,
        npcId: interaction.npcId,
        lookAt: interaction.lookAt,
        animation: interaction.animation,
        messageBubbleStyle: interaction.messageBubbleStyle,
        properties: interaction.properties ?? requiredMessageBubbleProperties,
    };
}

export const messageBubbleInteraction = (): InteractionFactoryEntity => {
    const component = <MessageBubblePreview />;

    const create = (getKeys: (amount: number) => string[]): IMessageBubble => {
        const keys = getKeys(1);
        return {
            id: uuidv4(),
            name: "Message Bubble",
            type: InteractionType.MessageBubble,
            text: keys[0],
            npcId: "player",
            lookAt: "",
            animation: { ...defaultMessageBubbleAnimation },
            messageBubbleStyle: messageBubbleStyles[0],
            properties: requiredMessageBubbleProperties,
        };
    };
    const migrate = (from: number, to: number, interaction: Interaction): IMessageBubble => {
        const messageBubble = interaction as IMessageBubble;

        if (interaction.properties === undefined) {
            return { ...messageBubble, properties: requiredMessageBubbleProperties };
        }

        const newProperties: InteractionProperty[] = GetMigratedProperties(
            requiredMessageBubbleProperties,
            interaction.properties
        );

        return { ...messageBubble, properties: newProperties };
    };

    const copy = (interaction: Interaction,
        getKeys: (amount: number) => string[],
        replaceKey: (from: string, to: string) => void, getAnswerArray: (originalAnswers: IAnswer[], newKeys: string[]) => IAnswer[]): IMessageBubble => {

        const messageBubble = interaction as IMessageBubble;
        const newKeys = getKeys(1);
        replaceKey(messageBubble.text, newKeys[0]);

        return {
            ...messageBubble,
            id: uuidv4(),
            text: newKeys[0],
        }
    }

    const validate = (interaction: Interaction, getValue: (key: string) => string, take: Take, actorPresets: Actor[], allPropPresets: Prop[] = []): string[] => {
        const take3d = take as Take3D;
        const converted = interaction as IMessageBubble;
        const warnings: string[] = [];

        ValidationHelper.ValidateKey(converted.text, getValue, warnings, "Message");
        validateBubbleAnchorInTake(converted.npcId, take3d, actorPresets, allPropPresets, warnings, converted.id);
        ValidationHelper.ValidateNpcInTake(converted.lookAt, take3d, actorPresets, warnings, getLookAtTargetMissingWarning);

        return warnings;
    }

    const validateErrors = (interaction: Interaction): string[] => {
        const converted = interaction as IMessageBubble;
        const errors: string[] = [];

        if (!converted.text?.trim()) errors.push("Message is required");
        if (!converted.npcId?.trim()) errors.push("Actor or Prop is required");
        if (!getMessageBubbleAnimationSearchValue(converted.animation)?.trim()) errors.push("Animation is required");
        if (!converted.messageBubbleStyle?.trim()) {
            errors.push("Message style is required");
        }

        return errors;
    }

    const filter = (
        interaction: Interaction,
        takeName: string,
        getMatchingResults: (
            searchableStrings: string[],
            takeName: string,
            interaction: Interaction,
            filterType: string
        ) => SearchResultData[],
        _getValue: (key: string) => string,
        searchLookupContext?: SearchLookupContext,
    ): SearchResultData[] => {

        const converted = interaction as IMessageBubble;
        const searchableStrings = compactSearchableStrings(
            interaction.name,
            converted.text,
            searchLookupContext?.getObjectSearchTerms(converted.npcId) ?? converted.npcId,
            searchLookupContext?.getObjectSearchTerms(converted.lookAt) ?? converted.lookAt,
            getMessageBubbleAnimationSearchValue(converted.animation),
            converted.messageBubbleStyle,
        );

        const resultsMessages = getMatchingResults(
            searchableStrings,
            takeName,
            interaction,
            filterType,
        );

        const actorSearchTerms = searchLookupContext?.getActorSearchTerms(converted.npcId) ?? [];
        const isActorAnchor = !searchLookupContext || actorSearchTerms.length > 1;
        if (isActorAnchor) {
            resultsMessages.forEach((message) => { message.profilePicture = converted.npcId });
        }

        return resultsMessages;
    };

    const filterType: string = "Message Bubble";

    return {
        View: component,
        NodeView: (nodeId: string, interaction: Interaction) => (
            <MessageBubbleNodePreview nodeId={nodeId} interaction={interaction} />
        ),
        FilterType: filterType,
        Create: create,
        Migrate: migrate,
        Copy: copy,
        Validate: validate,
        ValidateErrors: validateErrors,
        Filter: filter,
    };
};


